import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../api';
import { useSubscription } from '../hooks/useSubscription';

const T = {
  bg:'#0d0b1e', card:'#151230', elem:'#1c1640', border:'#2d1b69',
  violet:'#a78bfa', violetDark:'#7c3aed', green:'#4ade80', red:'#f87171',
  amber:'#fbbf24', text:'#e2e8f0', muted:'#6b7280',
};

export default function ChatAssistente() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'ticket-form' | 'ticket-list' | 'ticket-chat'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ticketData, setTicketData] = useState({ titolo:'', descrizione:'', priorita:'normale' });
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticketInput, setTicketInput] = useState('');
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const ticketMsgEndRef = useRef(null);

  const headers = { 'Content-Type':'application/json', 'Accept':'application/json', Authorization:`Bearer ${token}` };

  useEffect(() => {
    if (messages.length === 0 && user) {
      setMessages([{ role:'assistant', content:`Ciao ${user?.discord_username || user?.name || ''}! Sono l'assistente AI di Impero Elite.\n\nPuoi chiedermi:\n• Come usare la Dashboard\n• Consigli su Vinted e eBay\n• Domande sul corso\n• Problemi tecnici` }]);
    }
  }, [user]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);
  useEffect(() => { ticketMsgEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [ticketMessages]);

  // Check unread every 30s
  useEffect(() => {
    if (!token) return;
    const check = () => fetch(`${API_BASE}/tickets/unread-count`, { headers }).then(r=>r.json()).then(d=>setUnread(d.unread||0)).catch(()=>{});
    check();
    const iv = setInterval(check, 30000);
    return () => clearInterval(iv);
  }, [token]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role:'user', content:userMsg }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/chat/message`, { method:'POST', headers, body:JSON.stringify({ message:userMsg }) });
      if (res.status === 429) {
        const err = await res.json();
        setMessages(prev => [...prev, { role:'assistant', content:err.detail || 'Limite giornaliero raggiunto. Riprova domani o apri un ticket.' }]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      setMessages(prev => [...prev, { role:'assistant', content:data.response || 'Errore. Riprova.' }]);
    } catch { setMessages(prev => [...prev, { role:'assistant', content:'Errore di connessione.' }]); }
    finally { setLoading(false); }
  };

  const sendTicket = async () => {
    if (!ticketData.titolo || !ticketData.descrizione) return;
    try {
      await fetch(`${API_BASE}/tickets`, { method:'POST', headers, body:JSON.stringify(ticketData) });
      setView('chat');
      setMessages(prev => [...prev, { role:'assistant', content:'Ticket inviato! Il team ti rispondera presto. Puoi controllare lo stato dai tuoi ticket.' }]);
      setTicketData({ titolo:'', descrizione:'', priorita:'normale' });
    } catch { alert('Errore invio ticket'); }
  };

  const loadTickets = async () => {
    try {
      const res = await fetch(`${API_BASE}/tickets`, { headers });
      const data = await res.json();
      setTickets(data);
    } catch {}
  };

  const openTicketChat = async (ticket) => {
    setActiveTicket(ticket);
    setView('ticket-chat');
    try {
      const res = await fetch(`${API_BASE}/tickets/${ticket.id}/messages`, { headers });
      const data = await res.json();
      setTicketMessages(data);
    } catch { setTicketMessages([]); }
  };

  const sendTicketMessage = async () => {
    if (!ticketInput.trim() || !activeTicket) return;
    try {
      await fetch(`${API_BASE}/tickets/${activeTicket.id}/messages`, {
        method:'POST', headers,
        body:JSON.stringify({ ticket_id:activeTicket.id, content:ticketInput.trim() })
      });
      setTicketInput('');
      const res = await fetch(`${API_BASE}/tickets/${activeTicket.id}/messages`, { headers });
      setTicketMessages(await res.json());
    } catch {}
  };

  // Poll ticket messages every 5s when in ticket-chat view
  useEffect(() => {
    if (view !== 'ticket-chat' || !activeTicket) return;
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/tickets/${activeTicket.id}/messages`, { headers });
        setTicketMessages(await res.json());
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [view, activeTicket]);

  const { isPro } = useSubscription();
  const [bubbleSide, setBubbleSide] = useState('right');
  const dragRef = useRef({ startX: 0, currentX: 0, moved: false });

  const onBubbleTouchStart = useCallback((e) => {
    dragRef.current = { startX: e.touches[0].clientX, currentX: e.touches[0].clientX, moved: false };
  }, []);

  const onBubbleTouchMove = useCallback((e) => {
    const x = e.touches[0].clientX;
    if (Math.abs(x - dragRef.current.startX) > 8) dragRef.current.moved = true;
    dragRef.current.currentX = x;
  }, []);

  const onBubbleTouchEnd = useCallback(() => {
    if (!dragRef.current.moved) return;
    const side = dragRef.current.currentX < window.innerWidth / 2 ? 'left' : 'right';
    setBubbleSide(side);
  }, []);

  if (!user || !isPro) return null;

  const statoBadge = (s) => {
    const colors = { aperto:T.red, in_lavorazione:T.amber, risolto:T.green };
    const labels = { aperto:'Aperto', in_lavorazione:'In lavorazione', risolto:'Risolto' };
    return { color:colors[s]||T.muted, label:labels[s]||s };
  };

  const msgBubble = (role, content, key) => (
    <div key={key} style={{display:'flex',justifyContent:role==='user'?'flex-end':'flex-start',gap:6,alignItems:'flex-end'}}>
      {role!=='user' && <div style={{width:22,height:22,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.6rem',flexShrink:0}}>🤖</div>}
      <div style={{
        maxWidth:'80%',padding:'9px 13px',
        borderRadius:role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',
        background:role==='user'?'linear-gradient(135deg,#7c3aed,#4f46e5)':T.elem,
        border:role==='user'?'none':`1px solid ${T.border}`,
        color:T.text,fontSize:'0.8rem',fontFamily:'DM Sans,sans-serif',lineHeight:1.5,whiteSpace:'pre-wrap'
      }}>{content}</div>
    </div>
  );

  return (
    <>
      {/* BUBBLE */}
      <div onClick={()=>{if(dragRef.current.moved)return;setIsOpen(!isOpen);if(!isOpen)setUnread(0);}}
        onTouchStart={onBubbleTouchStart} onTouchMove={onBubbleTouchMove} onTouchEnd={onBubbleTouchEnd}
        style={{
        position:'fixed',bottom:24,
        ...(bubbleSide === 'right' ? { right: 24 } : { left: 24 }),
        zIndex:1000,width:52,height:52,borderRadius:16,
        background:T.card,border:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'center',
        cursor:'pointer',boxShadow:'0 4px 24px rgba(0,0,0,0.4)',transition:'all 0.3s ease',
        touchAction:'none',
      }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.05)';e.currentTarget.style.borderColor='#7c3aed';e.currentTarget.style.boxShadow='0 0 20px rgba(124,58,237,0.3)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';e.currentTarget.style.borderColor=T.border;e.currentTarget.style.boxShadow='0 4px 24px rgba(0,0,0,0.4)';}}>
        {isOpen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 10h.01M12 10h.01M16 10h.01" strokeLinecap="round"/></svg>
        )}
        {unread > 0 && !isOpen && <div style={{position:'absolute',top:-6,right:-6,width:18,height:18,borderRadius:'50%',background:T.red,color:'white',fontSize:'0.6rem',fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',border:`2px solid ${T.bg}`}}>{unread}</div>}
      </div>

      {/* PANEL */}
      {isOpen && (
        <div style={{position:'fixed',bottom:90,...(bubbleSide === 'right' ? { right: 24 } : { left: 24 }),zIndex:1000,width:Math.min(360, window.innerWidth - 32),height:520,maxHeight:'80vh',background:T.card,border:`1px solid ${T.border}`,borderRadius:20,display:'flex',flexDirection:'column',boxShadow:'0 8px 32px rgba(0,0,0,0.5)',animation:'fadeInUp 0.3s ease',overflow:'hidden'}}>
          {/* HEADER */}
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T.border}`,background:'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(79,70,229,0.06))',display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:32,height:32,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.95rem',flexShrink:0}}>🤖</div>
            <button onClick={()=>setIsOpen(false)} style={{position:'absolute',top:10,right:12,background:'none',border:'none',color:'#6b7280',fontSize:'1.2rem',cursor:'pointer',zIndex:10,padding:4,lineHeight:1}} title="Chiudi">✕</button>
            <div style={{flex:1}}>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:'0.85rem',color:T.text}}>Impero Elite</div>
              <div style={{fontSize:'0.62rem',color:T.green,display:'flex',alignItems:'center',gap:3}}>
                <span style={{width:5,height:5,borderRadius:'50%',background:T.green,display:'inline-block',animation:'pulse 2s infinite'}}/>Online
              </div>
            </div>
            <button onClick={(e)=>{e.stopPropagation();setView('ticket-list');loadTickets();}} style={{background:'rgba(167,139,250,0.1)',border:`1px solid ${T.border}`,borderRadius:9999,padding:'4px 10px',color:T.violet,fontSize:'0.62rem',fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
              🎫 I miei ticket
            </button>
            <button onClick={(e)=>{e.stopPropagation();setView('ticket-form');}} style={{background:`rgba(251,191,36,0.1)`,border:`1px solid ${T.amber}`,borderRadius:9999,padding:'4px 10px',color:T.amber,fontSize:'0.62rem',fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>
              + Ticket
            </button>
          </div>

          {/* TAB BAR */}
          {view !== 'chat' && view !== 'ticket-chat' && (
            <div style={{display:'flex',borderBottom:`1px solid ${T.border}`}}>
              <button onClick={()=>setView('chat')} style={{flex:1,padding:'8px',fontSize:'0.72rem',fontWeight:600,color:view==='chat'?T.violet:T.muted,background:'transparent',border:'none',cursor:'pointer',borderBottom:view==='chat'?`2px solid ${T.violet}`:'2px solid transparent',fontFamily:'DM Sans,sans-serif'}}>💬 Chat AI</button>
              <button onClick={()=>{setView('ticket-list');loadTickets();}} style={{flex:1,padding:'8px',fontSize:'0.72rem',fontWeight:600,color:view==='ticket-list'?T.violet:T.muted,background:'transparent',border:'none',cursor:'pointer',borderBottom:view==='ticket-list'?`2px solid ${T.violet}`:'2px solid transparent',fontFamily:'DM Sans,sans-serif'}}>🎫 Ticket</button>
              <button onClick={()=>setView('ticket-form')} style={{flex:1,padding:'8px',fontSize:'0.72rem',fontWeight:600,color:view==='ticket-form'?T.violet:T.muted,background:'transparent',border:'none',cursor:'pointer',borderBottom:view==='ticket-form'?`2px solid ${T.violet}`:'2px solid transparent',fontFamily:'DM Sans,sans-serif'}}>+ Nuovo</button>
            </div>
          )}

          {/* === CHAT AI === */}
          {view === 'chat' && (
            <>
              <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:8}}>
                {messages.map((msg,i) => msgBubble(msg.role, msg.content, i))}
                {loading && (
                  <div style={{display:'flex',gap:6,alignItems:'flex-end'}}>
                    <div style={{width:22,height:22,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.6rem'}}>🤖</div>
                    <div style={{padding:'9px 13px',borderRadius:'16px 16px 16px 4px',background:T.elem,border:`1px solid ${T.border}`}}>
                      <div style={{display:'flex',gap:4}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:'50%',background:T.violet,animation:`pulse 1s ${i*0.2}s infinite`}}/>)}</div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef}/>
              </div>
              <div style={{padding:'10px 14px',borderTop:`1px solid ${T.border}`,display:'flex',gap:8}}>
                <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}}
                  placeholder="Scrivi un messaggio..." style={{flex:1,background:T.elem,border:`1px solid ${T.border}`,borderRadius:9999,padding:'9px 14px',color:T.text,fontFamily:'DM Sans,sans-serif',fontSize:'0.82rem',outline:'none'}}/>
                <button onClick={sendMessage} disabled={loading||!input.trim()} style={{width:38,height:38,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.95rem',color:'white',opacity:loading||!input.trim()?0.5:1}}>➤</button>
              </div>
            </>
          )}

          {/* === TICKET LIST === */}
          {view === 'ticket-list' && (
            <div style={{flex:1,overflowY:'auto',padding:14}}>
              {tickets.length === 0 ? (
                <div style={{textAlign:'center',padding:30,color:T.muted,fontSize:'0.82rem'}}>
                  <div style={{fontSize:'2rem',marginBottom:8}}>📭</div>
                  Nessun ticket aperto
                </div>
              ) : tickets.map(t => {
                const sb = statoBadge(t.stato);
                return (
                  <div key={t.id} onClick={()=>openTicketChat(t)} style={{background:T.elem,border:`1px solid ${T.border}`,borderRadius:12,padding:12,marginBottom:8,cursor:'pointer',transition:'border-color 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=T.violetDark} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <span style={{fontWeight:600,fontSize:'0.82rem',color:T.text}}>{t.titolo}</span>
                      <span style={{fontSize:'0.62rem',fontWeight:700,color:sb.color,background:`${sb.color}15`,padding:'2px 8px',borderRadius:9999,border:`1px solid ${sb.color}40`}}>{sb.label}</span>
                    </div>
                    <div style={{fontSize:'0.7rem',color:T.muted}}>#{t.id} • {new Date(t.created_at).toLocaleDateString('it-IT')}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* === TICKET CHAT === */}
          {view === 'ticket-chat' && activeTicket && (
            <>
              <div style={{padding:'10px 14px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8}}>
                <button onClick={()=>{setView('ticket-list');loadTickets();}} style={{background:'none',border:'none',color:T.violet,cursor:'pointer',fontSize:'0.82rem',fontFamily:'DM Sans,sans-serif'}}>← Indietro</button>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:'0.8rem',color:T.text}}>{activeTicket.titolo}</div>
                  <div style={{fontSize:'0.62rem',color:T.muted}}>Ticket #{activeTicket.id}</div>
                </div>
                <span style={{fontSize:'0.6rem',fontWeight:700,color:statoBadge(activeTicket.stato).color}}>{statoBadge(activeTicket.stato).label}</span>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:8}}>
                {/* Descrizione originale */}
                <div style={{background:T.elem,border:`1px solid ${T.border}`,borderRadius:12,padding:10,fontSize:'0.75rem',color:T.muted,marginBottom:4}}>
                  <div style={{fontWeight:600,color:T.violet,fontSize:'0.65rem',textTransform:'uppercase',marginBottom:4}}>Descrizione ticket</div>
                  {activeTicket.descrizione}
                </div>
                {ticketMessages.map((m,i) => (
                  <div key={i} style={{display:'flex',justifyContent:m.sender_role==='user'?'flex-end':'flex-start',gap:6,alignItems:'flex-end'}}>
                    {m.sender_role!=='user' && <div style={{width:22,height:22,borderRadius:'50%',background:T.amber,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.6rem',flexShrink:0}}>👤</div>}
                    <div style={{
                      maxWidth:'80%',padding:'9px 13px',
                      borderRadius:m.sender_role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',
                      background:m.sender_role==='user'?'linear-gradient(135deg,#7c3aed,#4f46e5)':T.elem,
                      border:m.sender_role==='user'?'none':`1px solid ${T.amber}40`,
                      color:T.text,fontSize:'0.8rem',fontFamily:'DM Sans,sans-serif',lineHeight:1.5,whiteSpace:'pre-wrap'
                    }}>
                      {m.sender_role !== 'user' && <div style={{fontSize:'0.6rem',color:T.amber,fontWeight:600,marginBottom:3}}>Staff Impero Elite</div>}
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={ticketMsgEndRef}/>
              </div>
              {activeTicket.stato !== 'risolto' && (
                <div style={{padding:'10px 14px',borderTop:`1px solid ${T.border}`,display:'flex',gap:8}}>
                  <input value={ticketInput} onChange={e=>setTicketInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();sendTicketMessage();}}}
                    placeholder="Rispondi al ticket..." style={{flex:1,background:T.elem,border:`1px solid ${T.border}`,borderRadius:9999,padding:'9px 14px',color:T.text,fontFamily:'DM Sans,sans-serif',fontSize:'0.82rem',outline:'none'}}/>
                  <button onClick={sendTicketMessage} disabled={!ticketInput.trim()} style={{width:38,height:38,borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.95rem',color:'white',opacity:!ticketInput.trim()?0.5:1}}>➤</button>
                </div>
              )}
            </>
          )}

          {/* === TICKET FORM === */}
          {view === 'ticket-form' && (
            <div style={{flex:1,overflowY:'auto',padding:16,display:'flex',flexDirection:'column',gap:12}}>
              <div style={{textAlign:'center',marginBottom:4}}>
                <div style={{fontSize:'1.5rem',marginBottom:6}}>🎫</div>
                <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,color:T.text,fontSize:'0.92rem'}}>Apri un Ticket</div>
                <div style={{fontSize:'0.72rem',color:T.muted}}>Il team ti rispondera il prima possibile</div>
              </div>
              <div>
                <label style={{fontSize:'0.7rem',color:T.violet,fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:4}}>Titolo</label>
                <input value={ticketData.titolo} onChange={e=>setTicketData(p=>({...p,titolo:e.target.value}))}
                  placeholder="Descrivi brevemente" style={{width:'100%',background:T.elem,border:`1px solid ${T.border}`,borderRadius:10,padding:'9px 12px',color:T.text,fontFamily:'DM Sans,sans-serif',fontSize:'0.8rem',outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'0.7rem',color:T.violet,fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:4}}>Descrizione</label>
                <textarea value={ticketData.descrizione} onChange={e=>setTicketData(p=>({...p,descrizione:e.target.value}))}
                  placeholder="Spiega il problema..." rows={4}
                  style={{width:'100%',background:T.elem,border:`1px solid ${T.border}`,borderRadius:10,padding:'9px 12px',color:T.text,fontFamily:'DM Sans,sans-serif',fontSize:'0.8rem',outline:'none',resize:'none',boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:'0.7rem',color:T.violet,fontWeight:600,textTransform:'uppercase',display:'block',marginBottom:4}}>Priorita</label>
                <select value={ticketData.priorita} onChange={e=>setTicketData(p=>({...p,priorita:e.target.value}))}
                  style={{width:'100%',background:T.elem,border:`1px solid ${T.border}`,borderRadius:10,padding:'9px 12px',color:T.text,fontFamily:'DM Sans,sans-serif',fontSize:'0.8rem',outline:'none'}}>
                  <option value="bassa">Bassa</option>
                  <option value="normale">Normale</option>
                  <option value="alta">Alta</option>
                </select>
              </div>
              <div style={{display:'flex',gap:8,marginTop:4}}>
                <button onClick={()=>setView('chat')} style={{flex:1,background:T.elem,border:`1px solid ${T.border}`,borderRadius:9999,padding:10,color:T.text,fontFamily:'DM Sans,sans-serif',fontWeight:600,fontSize:'0.8rem',cursor:'pointer'}}>Annulla</button>
                <button onClick={sendTicket} style={{flex:2,background:'linear-gradient(135deg,#7c3aed,#4f46e5)',border:'none',borderRadius:9999,padding:10,color:'white',fontFamily:'DM Sans,sans-serif',fontWeight:700,fontSize:'0.8rem',cursor:'pointer'}}>Invia Ticket</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
